import { useEffect, useState } from 'react';
import type { Me } from '../lib/api';

/**
 * M4 — Migration Tracker Sheet controls (the four actions from the design):
 *   1) Authorized Users (email allowlist)   -> /api/tracker-sync/authorized
 *   2) Realtime sync status                 -> /api/tracker-sync/history
 *   3) Export to Excel (all tabs + live)    -> /api/migration/{tracker,refsheet} + SheetJS
 *
 * Structure pushes (new columns / tabs) are published from the Cowork space via
 * tools/push_structure.py, so there is no in-app structure editor here.
 *
 * Calls go through the gateway: api.trinethub.com must route /api/tracker-sync/*
 * to trinethub-tracker-sync-svc (see services/tracker-sync-svc/README).
 */

const ADMIN_COLS = [
  { key: 'status', label: 'Migration Status' }, { key: 'assigned_tech', label: 'Assigned Tech' },
  { key: 'cutover_date', label: 'Scheduled Cutover' }, { key: 'verified_by', label: 'Verified By' },
  { key: 'notes', label: 'Admin Notes' },
];
const SHEETJS_CDN = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';

function j(url: string, init?: RequestInit) {
  return fetch(url, { credentials: 'include', ...(init || {}) }).then((r) =>
    r.json().then((b) => ({ ok: r.ok, status: r.status, body: b })).catch(() => ({ ok: r.ok, status: r.status, body: {} as any })));
}

export default function TrackerSyncPanel({ me, apiBase }: { me: Me; apiBase: string }) {
  const isAdmin = me.role === 'super_admin' || me.role === 'tenant_admin';
  return (
    <div className="grid md:grid-cols-2 gap-3">
      <AuthorizedUsers apiBase={apiBase} isAdmin={isAdmin} />
      <SyncStatus apiBase={apiBase} />
      <ExportExcel apiBase={apiBase} />
      <SyncActivity apiBase={apiBase} />
    </div>
  );
}

function Card({ title, children }: { title: string; children: any }) {
  return (
    <div className="card p-3">
      <div className="text-sm font-semibold mb-2">{title}</div>
      {children}
    </div>
  );
}

// Is the OneDrive Master in sync with TriNetHub, and who is editing right now?
// Derived from the audit history: last source-sync (data freshness) + recent human edits.
function SyncActivity({ apiBase }: { apiBase: string }) {
  const [hist, setHist] = useState<any[]>([]);
  const load = () => j(`${apiBase}/api/tracker-sync/history?limit=200`).then((r) => setHist(r.body.history || []));
  useEffect(() => { load(); const t = setInterval(load, 60000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);
  const now = Date.now();
  const maxTs = (a: string) => hist.filter((h) => h.action === a).reduce((m, h) => Math.max(m, h.ts || 0), 0);
  const lastSync = maxTs('source_sync');
  const lastEdit = Math.max(maxTs('edit'), maxTs('revert'));
  const inSync = !!lastSync && (now - lastSync) < 20 * 60000;
  const ago = (ts: number) => { if (!ts) return '—'; const m = Math.round((now - ts) / 60000); return m < 1 ? 'just now' : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : `${Math.round(m / 1440)}d ago`; };
  const recent: Record<string, number> = {};
  hist.forEach((h) => {
    const e = String(h.editor_email || '');
    if ((h.action === 'edit' || h.action === 'revert') && e && !e.startsWith('source:') && h.ts && (now - h.ts) < 60 * 60000)
      recent[e] = Math.max(recent[e] || 0, h.ts);
  });
  const editors = Object.entries(recent).sort((a, b) => b[1] - a[1]);
  return (
    <Card title="Master ↔ TriNetHub sync">
      <div className="flex items-center gap-2 mb-1">
        <span className={'inline-block w-2.5 h-2.5 rounded-full ' + (inSync ? 'bg-green-500' : 'bg-amber-500')} />
        <span className="text-sm font-medium">{inSync ? 'In sync' : (lastSync ? 'Sync delayed' : 'Awaiting sync')}</span>
        <span className="text-xs text-gray-400">· data refreshed {ago(lastSync)}</span>
      </div>
      <p className="text-xs text-gray-500 mb-2">Last edit {ago(lastEdit)}. The OneDrive Master pulls this data every 15 min.</p>
      <div className="text-xs text-gray-500 mb-1">Working on it now (last hour)</div>
      {editors.length === 0 && <div className="text-xs text-gray-400">No active editors right now.</div>}
      <div className="max-h-24 overflow-auto text-xs">
        {editors.map(([email, ts]) => (
          <div key={email} className="flex items-center justify-between py-0.5">
            <span className="font-mono truncate" title={email}>
              <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-1.5 align-middle" />{email}
            </span>
            <span className="text-gray-400 shrink-0">{ago(ts)}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function AuthorizedUsers({ apiBase, isAdmin }: { apiBase: string; isAdmin: boolean }) {
  const [list, setList] = useState<any[]>([]);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('editor');
  const [msg, setMsg] = useState('');
  const load = () => j(`${apiBase}/api/tracker-sync/authorized`).then((r) => setList(r.body.authorized || []));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const add = () => {
    if (!email.includes('@')) { setMsg('Enter a valid email'); return; }
    setMsg('Adding…');
    j(`${apiBase}/api/tracker-sync/authorized`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, role }) })
      .then((r) => { setMsg(r.ok ? 'Added' : 'Error: ' + (r.body.error || r.status)); setEmail(''); load(); });
  };
  const remove = (e: string) => { setMsg('Removing…'); j(`${apiBase}/api/tracker-sync/authorized?email=${encodeURIComponent(e)}`, { method: 'DELETE' }).then((r) => { setMsg(r.ok ? 'Removed' : 'Error'); load(); }); };
  return (
    <Card title="Authorized Users (email list)">
      {isAdmin && (
        <div className="flex gap-2 mb-2">
          <input className="input-sm flex-1" placeholder="user@longbeach.gov" value={email} onChange={(e) => setEmail(e.target.value)} />
          <select className="input-sm" value={role} onChange={(e) => setRole(e.target.value)} title="Access level">
            <option value="editor">Full Access</option>
            <option value="viewer">Read only</option>
          </select>
          <button className="btn-secondary" onClick={add}>Add</button>
        </div>
      )}
      <div className="max-h-40 overflow-auto text-xs">
        {list.filter((u) => u.active).length === 0 && <div className="text-gray-400">No authorized editors yet.</div>}
        {list.filter((u) => u.active).map((u) => (
          <div key={u.email} className="flex items-center justify-between py-0.5 border-b border-tnh-border/30 dark:border-tnh-border-dark/30">
            <span className="font-mono">{u.email}<span className="text-gray-400"> · {u.role === 'viewer' ? 'Read only' : u.role === 'admin' ? 'Admin' : 'Full Access'}</span></span>
            {isAdmin && <button className="text-red-500 hover:underline" onClick={() => remove(u.email)}>remove</button>}
          </div>
        ))}
      </div>
      {msg && <div className="text-xs text-gray-500 mt-1">{msg}</div>}
    </Card>
  );
}

function SyncStatus({ apiBase }: { apiBase: string }) {
  const [hist, setHist] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const load = () => { setLoading(true); j(`${apiBase}/api/tracker-sync/history?limit=15`).then((r) => setHist(r.body.history || [])).finally(() => setLoading(false)); };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); /* eslint-disable-next-line */ }, []);
  const last = hist.length ? new Date(hist[0].ts).toLocaleString() : '—';
  return (
    <Card title="Realtime sync status">
      <div className="text-xs mb-2">
        <span className={`inline-block w-2 h-2 rounded-full mr-1 ${hist.length ? 'bg-green-500' : 'bg-gray-400'}`} />
        Last edit: {last} · <button className="text-tnh-accent hover:underline" onClick={load}>{loading ? 'refreshing…' : 'refresh'}</button>
      </div>
      <div className="max-h-40 overflow-auto text-[11px]">
        {hist.length === 0 && <div className="text-gray-400">No sync activity yet.</div>}
        {hist.map((h, i) => (
          <div key={i} className="py-0.5 border-b border-tnh-border/30 dark:border-tnh-border-dark/30">
            <span className={h.action === 'reject' ? 'text-red-500' : h.action === 'revert' ? 'text-amber-600' : 'text-green-600'}>{h.action}</span>
            {' '}<span className="font-mono">{h.column_key}</span> = {String(h.new_value || '').slice(0, 24)}
            <span className="text-gray-400"> · {h.editor_email}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function loadSheetJS(): Promise<any> {
  return new Promise((resolve, reject) => {
    if ((window as any).XLSX) return resolve((window as any).XLSX);
    const s = document.createElement('script');
    s.src = SHEETJS_CDN;
    s.onload = () => resolve((window as any).XLSX);
    s.onerror = () => reject(new Error('Could not load SheetJS'));
    document.head.appendChild(s);
  });
}

function ExportExcel({ apiBase }: { apiBase: string }) {
  const [status, setStatus] = useState('');
  const run = async () => {
    try {
      setStatus('Loading export engine…');
      const XLSX = await loadSheetJS();
      setStatus('Fetching Master…');
      // Master (paginate)
      let off = 0, rows: any[] = [], cols: string[] = [];
      for (;;) {
        const r = await j(`${apiBase}/api/migration/tracker?offset=${off}&limit=500`);
        cols = r.body.cols || cols; (r.body.rows || []).forEach((x: any) => rows.push(x));
        off += 500; if (off >= (r.body.total || 0) || !(r.body.rows || []).length) break;
      }
      const header = [...ADMIN_COLS.map((a) => a.label), ...cols];
      const masterAoa = [header, ...rows.map((r) => [r.status, r.assigned_tech, r.cutover_date, r.verified_by, r.notes, ...(r.sys || [])])];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet(masterAoa), 'WebEx Master Migration');
      // Refsheet tabs
      const tabsRes = await j(`${apiBase}/api/migration/refsheet`);
      const tabs: string[] = tabsRes.body.tabs || [];
      for (const tab of tabs) {
        setStatus(`Fetching ${tab}…`);
        let o = 0, hdr: string[] = [], all: string[][] = [];
        for (;;) {
          const r = await j(`${apiBase}/api/migration/refsheet?tab=${encodeURIComponent(tab)}&offset=${o}&limit=1000`);
          hdr = r.body.header || hdr; (r.body.rows || []).forEach((x: string[]) => all.push(x));
          o += 1000; if (o >= (r.body.total || 0) || !(r.body.rows || []).length) break;
        }
        const name = tab.replace(/[\\/?*[\]:]/g, ' ').slice(0, 31);
        XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([hdr, ...all]), name);
      }
      setStatus('Building workbook…');
      XLSX.writeFile(wb, 'Collaboration Master Tracker.xlsx');
      setStatus('Downloaded ✓');
    } catch (e: any) { setStatus('Export failed: ' + e.message); }
  };
  return (
    <Card title="Export to Excel">
      <p className="text-xs text-gray-500 mb-2">Download the full workbook (Master + all reference tabs, with live gold columns) for upload to OneDrive.</p>
      <button className="btn-secondary" onClick={run}>Export workbook</button>
      {status && <div className="text-xs text-gray-500 mt-2">{status}</div>}
    </Card>
  );
}

// (Push structure config card removed — structure is now published from the Cowork
//  space via tools/push_structure.py, which posts to /api/tracker-sync/schema.)
